/**
 * Cross-subdomain SSO: hydrate localStorage from the domain cookie.
 *
 * The `.moyasolutions.com` cookie `moya_token` is set by the SSO portal on
 * login. Apps read their credential from `localStorage`, so something has to
 * carry it across. This is that something, and it runs at shell import time --
 * before any host app's router guard reads storage.
 *
 * THE SAFETY PROPERTY THIS IS BUILT AROUND: this only ever WRITES a token it
 * has positively verified is unexpired, and it never deletes or blanks
 * anything. It therefore cannot sign anyone out. That matters more than the bug
 * it fixes: this runs on import in every app that embeds the shell, so a
 * mistake here logs out the whole estate, which is far worse than one stale
 * token in one of them.
 *
 * WHAT WAS WRONG: hydration was guarded on `if (!existing)` -- copy the cookie
 * into localStorage ONLY when localStorage was empty. Signing in refreshes the
 * cookie, but a fresh cookie could never displace what was already stored, so a
 * browser kept serving the token from whenever it FIRST logged in, for as long
 * as its storage survived. It surfaced as a notification bell stuck on
 * "connecting" -- notification-service was correctly refusing the expired token
 * it was handed -- on two devices that had both signed in that day.
 *
 * `moya_user` was frozen the same way, which is the quieter half of the bug: a
 * role change or an organisation move never reached a browser until someone
 * cleared its storage.
 */

type Payload = Record<string, unknown>;

/** Decode a JWT payload, or null if it is not a readable JWT. */
function decodePayload(jwt: string): Payload | null {
  try {
    // JWT payloads are base64url-encoded (- _ instead of + /, no padding);
    // atob() only accepts standard base64, so convert and pad first ---
    // otherwise any token containing - or _ throws.
    const segment = jwt.split('.')[1] || '';
    if (!segment) return null;
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const payload = JSON.parse(atob(padded));
    return payload && typeof payload === 'object' ? (payload as Payload) : null;
  } catch {
    return null;
  }
}

/** Expiry in epoch seconds, or null when absent or unreadable. */
function expOf(payload: Payload | null): number | null {
  const exp = payload?.exp;
  return typeof exp === 'number' && Number.isFinite(exp) ? exp : null;
}

/** Derive the cached user record apps read out of `moya_user`. */
function writeUser(payload: Payload | null): void {
  if (!payload?.sub) return;
  const user = {
    id: payload.sub,
    sub: payload.sub,
    email: payload.email || '',
    name: payload.name || '',
    roles: payload.roles || [],
    permissions: payload.permissions || [],
    organization_id: payload.organization_id || null,
  };
  localStorage.setItem('moya_user', JSON.stringify(user));
  localStorage.setItem('user_id', String(payload.sub));
}

/**
 * Copy the SSO cookie into localStorage when — and only when — doing so is an
 * improvement. Never throws; a failure here must not break app startup.
 */
export function hydrateSessionFromCookie(): void {
  try {
    if (typeof document === 'undefined' || typeof localStorage === 'undefined') return;

    const match = document.cookie.match(/(?:^|;\s*)moya_token=([^;]*)/);
    if (!match) return;                       // no cookie -> leave storage alone
    const cookieToken = decodeURIComponent(match[1]);
    if (!cookieToken) return;                 // empty cookie -> leave storage alone

    const existing = localStorage.getItem('moya_token') || localStorage.getItem('token');
    const cookiePayload = decodePayload(cookieToken);
    const cookieExp = expOf(cookiePayload);
    const nowSeconds = Date.now() / 1000;

    if (!existing) {
      // Nothing stored: hydrate, exactly as before. Deliberately NOT gated on
      // being able to decode the cookie — an opaque credential we cannot read
      // is still better than none, and this is the pre-existing behaviour.
      localStorage.setItem('moya_token', cookieToken);
      localStorage.setItem('token', cookieToken);
      writeUser(cookiePayload);
      return;
    }

    if (existing === cookieToken) {
      // Same credential already stored. Still refresh the derived user: roles
      // and organization_id can change while the token does not, and a browser
      // that only ever wrote them once would never see it.
      writeUser(cookiePayload);
      return;
    }

    // Past here we are considering REPLACING a stored credential, so the bar is
    // positive proof that the cookie is better. Anything less and the stored
    // one stays: a session that still works is worth more than a working bell.
    if (cookieExp === null) return;           // unreadable cookie -> not proof
    if (cookieExp <= nowSeconds) return;      // expired cookie -> never adopt

    const existingExp = expOf(decodePayload(existing));

    // An unreadable stored token is replaced by a cookie we have just proven
    // valid: there is evidence for one and none for the other. A readable
    // stored token is replaced only if the cookie genuinely outlives it, so a
    // stale cookie cannot roll a fresher session backwards.
    if (existingExp !== null && cookieExp <= existingExp) return;

    localStorage.setItem('moya_token', cookieToken);
    localStorage.setItem('token', cookieToken);
    writeUser(cookiePayload);
  } catch {
    /* silent fail — don't break app startup */
  }
}
